import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup,
} from "react-bootstrap";

// ✅ BASE URL
const BASE = (() => {
    let url = process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";
    url = url.replace(/\/$/, "");
    if (url.endsWith("/api")) url = url.slice(0, -4);
    return url;
})();

const API_URL = `${BASE}/api/news`;

const Health = () => {
    const [healthNews, setHealthNews] = useState([]);
    const [show, setShow] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchHealthNews();
    }, []);

    // ✅ FIXED getImage — Cloudinary + Local + Fallback
    const getImage = (item) => {
        const placeholder = "https://placehold.co/1200x650?text=No+Image";
        if (!item) return placeholder;

        const resolveUrl = (path) => {
            if (!path || typeof path !== "string" || path.trim() === "") return null;
            const p = path.trim();

            // ✅ Full URL (Cloudinary / external)
            if (p.startsWith("http://") || p.startsWith("https://")) return p;

            // ✅ /uploads/filename.jpg
            if (p.startsWith("/uploads/")) return `${BASE}${p}`;

            // ✅ uploads/filename.jpg
            if (p.startsWith("uploads/")) return `${BASE}/${p}`;

            // ✅ just filename.jpg
            if (p.includes(".")) return `${BASE}/uploads/${p}`;

            return null;
        };

        return (
            resolveUrl(item.imagePath) ||
            resolveUrl(item.image) ||
            resolveUrl(item.imageUrl) ||
            resolveUrl(item.imageURL) ||
            resolveUrl(item.thumbnail) ||
            placeholder
        );
    };

    // ✅ Image Error Fallback
    const handleImgError = (e) => {
        e.target.onerror = null;
        e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
    };

    // ✅ VIEWS
    const getViews = (item) => {
        const v =
            item?.views ??
            item?.viewCount ??
            item?.totalViews ??
            item?.impressions ??
            0;
        return Number(v) || 0;
    };

    const formatViews = (n) => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
        return n;
    };

    // ✅ FETCH
    const fetchHealthNews = async () => {
        try {
            const res = await fetch(API_URL);
            const result = await res.json();
            const data = result?.data || [];

            const health = data.filter((item) =>
                ["health", "fitness", "medical"].includes(
                    item.category?.toLowerCase()
                )
            );

            setHealthNews(health);
        } catch (err) {
            console.error("Health Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // ✅ MODAL + VIEW COUNT
    const handleShow = (item) => {
        const id = item?._id || item?.id;
        const nextViews = getViews(item) + 1;

        if (id) {
            setHealthNews((prev) =>
                prev.map((n) =>
                    n._id === id || n.id === id
                        ? { ...n, views: getViews(n) + 1 }
                        : n
                )
            );
        }

        setSelectedNews({ ...item, views: nextViews });
        setShow(true);

        fetch(`${API_URL}/views/${id}`, { method: "PUT" }).catch(() => { });
    };

    const handleClose = () => {
        setShow(false);
        setSelectedNews(null);
    };

    // ✅ SHARE
    const getShareUrl = () => window.location.href;

    const openNewTab = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Link copied!");
        } catch {
            window.prompt("Copy this link:", text);
        }
    };

    const shareTo = (platform, post) => {
        if (!post) return;
        const url = getShareUrl();
        const title = (post?.title || "News").trim();
        const text = `${title} ${url}`;

        switch (platform) {
            case "whatsapp":
                openNewTab(`https://wa.me/?text=${encodeURIComponent(text)}`);
                break;
            case "facebook":
                openNewTab(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
                );
                break;
            case "x":
                openNewTab(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`
                );
                break;
            case "telegram":
                openNewTab(
                    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
                );
                break;
            case "linkedin":
                openNewTab(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
                );
                break;
            case "email":
                openNewTab(
                    `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`
                );
                break;
            case "copy":
                copyToClipboard(url);
                break;
            default:
                break;
        }
    };

    const ShareMenu = ({ post }) => (
        <Dropdown as={ButtonGroup}>
            <Button
                size="sm"
                variant="danger"
                onClick={() => shareTo("whatsapp", post)}
            >
                <i className="bi bi-share-fill me-1" />
                Share
            </Button>

            <Dropdown.Toggle split size="sm" variant="danger" />

            <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => shareTo("whatsapp", post)}>
                    <i className="bi bi-whatsapp me-2" />WhatsApp
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("facebook", post)}>
                    <i className="bi bi-facebook me-2" />Facebook
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("x", post)}>
                    <i className="bi bi-twitter-x me-2" />X (Twitter)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("telegram", post)}>
                    <i className="bi bi-telegram me-2" />Telegram
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("linkedin", post)}>
                    <i className="bi bi-linkedin me-2" />LinkedIn
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => shareTo("email", post)}>
                    <i className="bi bi-envelope-fill me-2" />Email
                </Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("copy", post)}>
                    <i className="bi bi-link-45deg me-2" />Copy Link
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );

    return (
        <>
            {/* HERO */}
            <div className="health-hero text-white text-center py-5">
                <Container>
                    <h1 className="fw-bold display-4">Health News</h1>
                    <p className="lead">
                        Stay informed with health tips, fitness trends & medical updates
                    </p>
                </Container>
            </div>

            {/* NEWS */}
            <Container className="py-5">
                {loading ? null : healthNews.length === 0 ? (
                    <h5 className="text-center text-muted">
                        No Health News Available
                    </h5>
                ) : (
                    <Row className="g-4">
                        {healthNews.map((news, index) => (
                            <Col md={4} sm={6} key={news._id || news.id || index}>
                                <Card className="health-card h-100">

                                    <div className="health-img-wrap">
                                        {/* ✅ FIXED IMAGE */}
                                        <Card.Img
                                            src={getImage(news)}
                                            className="health-img"
                                            alt={news.title}
                                            onError={handleImgError}
                                        />
                                    </div>

                                    <Card.Body>
                                        <div className="d-flex justify-content-between mb-2">
                                            <Badge bg="danger">
                                                {news.category}
                                            </Badge>

                                            <span className="news-views">
                                                <i className="bi bi-eye me-1" />
                                                {formatViews(getViews(news))}
                                            </span>
                                        </div>

                                        <Card.Title>{news.title}</Card.Title>

                                        <Card.Text className="text-muted news-description">
                                            {news.shortDesc}
                                        </Card.Text>

                                        <div className="d-flex gap-2 flex-wrap">
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => handleShow(news)}
                                            >
                                                Read More
                                            </Button>

                                            <ShareMenu post={news} />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            {/* MODAL */}
            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedNews && (
                        <>
                            {/* ✅ FIXED IMAGE */}
                            <img
                                src={getImage(selectedNews)}
                                className="w-100 rounded mb-3"
                                alt={selectedNews.title}
                                onError={handleImgError}
                            />

                            <p className="news-article-text">{selectedNews.content}</p>

                            <div className="d-flex justify-content-between mt-3">
                                <span className="news-views">
                                    <i className="bi bi-eye me-1" />
                                    {formatViews(getViews(selectedNews))}
                                </span>

                                <ShareMenu post={selectedNews} />
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {/* STYLE */}
            <style>{`
                .health-hero {
                    background: linear-gradient(135deg, #dc3545, #6c0d14);
                }

                .health-card {
                    border-radius: 14px;
                    transition: transform 0.3s, box-shadow 0.3s;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.08);
                }

                .health-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 18px 40px rgba(0,0,0,0.15);
                }

                .health-img-wrap {
                    overflow: hidden;
                    border-radius: 14px 14px 0 0;
                }

                .health-img {
                    height: 220px;
                    width: 100%;
                    object-fit: cover;
                    transition: transform 0.3s;
                }

                .health-card:hover .health-img {
                    transform: scale(1.04);
                }

                .news-views {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6c757d;
                }
            `}</style>
        </>
    );
};

export default Health;